<template>
  <div class="grid gap-5 sm:gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
    <div
      class="group border rounded-2xl shadow-sm border-ewhite-200 bg-white hover:shadow-xl hover:shadow-eorange-500/10 transition-all duration-300 hover:border-eorange-300 hover:-translate-y-1 overflow-hidden"
      v-for="provider in provider_list"
      v-bind:key="provider.id"
    >
      <div class="p-0 h-full flex flex-col">
        <div
          class="relative px-6 pt-6 pb-4 bg-gradient-to-br from-slate-50 to-white border-b border-slate-100"
        >
          <div class="flex items-start justify-between gap-3 mb-3">
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <div
                class="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-eblack-400 to-eblack-900 shadow-lg shadow-eblack-500/30 flex-shrink-0"
              >
                <IconUser class="h-6 w-6 text-white" />
              </div>
              <div class="flex-1 min-w-0">
                <h3
                  class="font-semibold text-base text-foreground leading-tight line-clamp-2 uppercase"
                >
                  {{ provider.name }}
                </h3>
              </div>
            </div>
            <span
              class="text-xs font-semibold whitespace-nowrap flex-shrink-0 px-4 py-1 border rounded-full bg-egreen-100 text-egreen-800 border-egreen-300 first-letter:uppercase"
            >
              {{ provider.state }}
            </span>
          </div>

          <div class="flex items-center gap-2 text-sm text-muted-foreground">
            <IconMapPin class="h-4 w-4 flex-shrink-0 text-slate-400" />
            <span class="leading-tight line-clamp-1 uppercase">
              {{
                `${provider.country_name}, ${provider.location_one}, ${provider.location_two}`
              }}
            </span>
          </div>
        </div>

        <div class="flex-1 px-6 py-5 space-y-4">
          <div class="grid grid-cols-2 gap-2">
            <div
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-ewhite-50 border border-ewhite-200"
            >
              <IconPhone class="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span class="text-sm font-medium text-slate-700 truncate">{{
                formatPhone(provider.primary_contact)
              }}</span>
            </div>
            <div
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200"
            >
              <IconMail class="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span class="text-sm font-medium text-slate-700 truncate">{{
                provider.email
              }}</span>
            </div>
          </div>

          <div
            class="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-eorange-50 to-rajah-50 border border-eorange-200"
          >
            <div class="flex-1">
              <p class="text-xs text-eorange-700 font-medium mb-0.5">{{ $t("provider.auto.servicios") }}</p>
              <p class="text-lg font-bold text-eorange-900">
                {{ provider.services.length }}
              </p>
            </div>
          </div>

          <div class="pt-2">
            <ExpandableText
              :text="provider.description"
              :fallback="$t('provider.auto.sin_descripcion_disponible')"
              :see-more-label="$t('provider.auto.ver_mas')"
              :see-less-label="$t('provider.auto.ver_menos')"
            />
          </div>
        </div>

        <div
          class="px-6 py-4 bg-ewhite-50/50 border-t border-ewhite-100 mt-auto"
        >
          <div class="flex gap-2">
            <button
              class="inline-flex flex-1 gap-1 p-3 text-sm text-white bg-eblue-500 rounded-xl items-center justify-center"
              type="button"
              @click="editProvider(provider)"
            >
              <IconEdit class="w-7 h-7" stroke="{2}" />
              <span>{{ $t("provider.auto.editar") }}</span>
            </button>
            <button
              class="inline-flex flex-1 items-center justify-center gap-1 p-3 text-sm text-white bg-eblack-600 rounded-xl"
              type="button"
              @click="handleShowDetails(provider)"
            >
              <IconEye class="w-7 h-7" stroke="{2}" />
              <span>{{ $t("provider.auto.ver_detalles") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import providesRequests from "@/services/admin/provider-service";
import {
  IconEdit,
  IconEye,
  IconMail,
  IconMapPin,
  IconPhone,
  IconUser,
} from "@tabler/icons-vue";
import { formatPhoneNumberForDisplay } from "@/shared/utils/phone-number";
import ExpandableText from "@/components/ui/ExpandableText.vue";
export default {
  props: ["search", "showModal", "filters", "orderBy", "setOverview"],
  name: "ProviderCard",
  data() {
    return {
      provider_list: [],
      providers: [],
    };
  },
  components: {
    IconEdit,
    IconEye,
    ExpandableText,
    IconUser,
    IconMapPin,
    IconPhone,
    IconMail,
  },
  watch: {
    orderBy: {
      handler(value) {
        this.applyOrderBy(value);
      },
      immediate: true,
    },
    filters: {
      handler(value) {
        this.applyFilter(value);
      },
      immediate: true,
      deep: true,
    },
    search: {
      handler(value) {
        this.applySearch(value);
      },
      immediate: true,
    },
  },
  computed: {},
  methods: {
    formatPhone(value) {
      return formatPhoneNumberForDisplay(value) || "Sin teléfono";
    },
    initData() {
      providesRequests.getAllProvider().then((res) => {
        if (res.status == 200) {
          let { data } = res;

          this.prepareOverviewData(data);

          let formatData = data.map((item) => {
            return {
              ...item,
              category: this.getProviderCategories(item.services),
            };
          });
          this.providers = formatData;
          this.provider_list = formatData;
        }
      });
    },
    editProvider(provider) {
      this.showModal(provider);
    },
    getProviderCategories(services) {
      let categories = services.map((service) => {
        if (service.category && service.category.name) {
          return service.category.name;
        }
      });
      let uniqueCategories = [...new Set(categories)];

      return uniqueCategories;
    },
    applyFilter(value) {
      this.provider_list = [...this.providers];
      if (value) {
        if (value.category && value.category != "") {
          this.provider_list = this.provider_list.filter((provider) =>
            provider.category.includes(value.category)
          );
        }

        if (value.country && value.country != "") {
          this.provider_list = this.provider_list.filter(
            (provider) => provider.country == value.country
          );
        }

        if (value.location_one && value.location_one != "") {
          this.provider_list = this.provider_list.filter(
            (provider) => provider.location_one == value.location_one
          );
        }

        if (value.location_two && value.location_two != "") {
          this.provider_list = this.provider_list.filter(
            (provider) => provider.location_two == value.location_two
          );
        }
      }
    },
    applyOrderBy(type) {
      if (type == "name") {
        this.provider_list.sort((a, b) => a.name.localeCompare(b.name));
      }
    },
    applySearch(value) {
      if (value != "") {
        this.provider_list = this.providers.filter(
          (provider) =>
            provider.name.toLowerCase().includes(value.toLowerCase()) ||
            provider.description.toLowerCase().includes(value.toLowerCase()) ||
            provider.country.toLowerCase().includes(value.toLowerCase())
        );
      } else {
        this.provider_list = this.providers;
      }
    },
    handleShowDetails(provider) {
      this.showModal(provider, true);
    },
    getCommissionFormat(provider) {
      return provider.commission_type === "PERCENTAGE"
        ? `${provider.commission_amount}%`
        : `$${provider.commission_amount}`;
    },
    prepareOverviewData(data) {
      let overview = {
        total: data.length,
        active: data.filter((item) => item.state === "active").length,
        inactive: data.filter((item) => item.state === "inactive").length,
      };

      this.setOverview(overview);
    },
  },
  mounted() {
    this.initData();
  },
};
</script>

<style lang="scss" scoped></style>
